import {type PlacementRegionInfo, RelayCreateInputSchema, type RelayEndpoint} from "@client/contracts";
import {zodResolver} from "@hookform/resolvers/zod";
import {type FC, useCallback, useEffect, useState} from "react";
import {useForm} from "react-hook-form";
import type z from "zod";
import {IssuedKey} from "../../com/IssuedKey";
import {rpc} from "../../store/rpcs.mjs";
import {RelayRow} from "./RelayRow";

export const Relays: FC = () => {
    const [endpoints, setEndpoints] = useState<RelayEndpoint[] | null>(null);
    const [regions, setRegions] = useState<PlacementRegionInfo[]>([]);
    const [issued, setIssued] = useState<string | null>(null);
    const [reason, setReason] = useState<string | null>(null);

    const form = useForm<z.input<typeof RelayCreateInputSchema>>({
        resolver: zodResolver(RelayCreateInputSchema),
    });

    const refresh = useCallback(async () => {
        const {relay} = await rpc.query({relay: {list: [{}]}});
        setEndpoints(relay.list);
    }, []);

    useEffect(() => {
        void refresh();
        void rpc.query({regions: {list: [{}]}}).then(({regions: result}) => setRegions(result.list));
    }, [refresh]);

    const onSubmit = form.handleSubmit(async (values) => {
        setReason(null);
        setIssued(null);

        try {
            const {relay} = await rpc.query({relay: {create: [values]}});
            setIssued(relay.create.apiUrl);
            form.reset();
            await refresh();
        } catch (error) {
            setReason(error instanceof Error ? error.message : "Не удалось создать relay");
        }
    });

    const {errors, isSubmitting} = form.formState;

    return (
        <>
            {issued && <IssuedKey apiUrl={issued} />}

            <article>
                <header>{"Новый relay"}</header>

                <form onSubmit={onSubmit}>
                    <fieldset className="grid">
                        <label>
                            {"Название"}
                            <input {...form.register("title")} placeholder="например, парсер цен" />
                            {errors.title && <small>{errors.title.message}</small>}
                        </label>
                        <label>
                            {"Регион"}
                            <select {...form.register("region")} defaultValue="">
                                <option value="" disabled>
                                    {"Выберите регион"}
                                </option>
                                {["aws", "gcp", "azure"].map((provider) => (
                                    <optgroup key={provider} label={provider.toUpperCase()}>
                                        {regions
                                            .filter((region) => region.provider === provider)
                                            .map((region) => (
                                                <option key={region.id} value={region.id}>
                                                    {`${region.location} · ${region.id}`}
                                                </option>
                                            ))}
                                    </optgroup>
                                ))}
                            </select>
                            {errors.region && <small>{errors.region.message}</small>}
                        </label>
                    </fieldset>

                    {reason && <small className="error">{reason}</small>}

                    <button disabled={isSubmitting} type="submit" aria-busy={isSubmitting}>
                        {isSubmitting ? "Разворачиваем worker..." : "Создать"}
                    </button>
                </form>
            </article>

            <article>
                <header>{"Мои relay"}</header>

                {endpoints === null && <p aria-busy="true">{"Загружаем..."}</p>}

                {endpoints?.length === 0 && <p>{"Пока ничего не создано."}</p>}

                {!!endpoints?.length && (
                    <small>
                        {"Полный адрес с ключом виден только в момент выдачи. Потеряли — нажмите «Новый ключ»."}
                    </small>
                )}

                {!!endpoints?.length && (
                    <table className="nowrap">
                        <thead>
                            <tr>
                                <th>{"Название"}</th>
                                <th>{"Регион"}</th>
                                <th>{"Статус"}</th>
                                <th className="right">{"Действия"}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {endpoints?.map((endpoint) => (
                                <RelayRow
                                    key={endpoint.id}
                                    endpoint={endpoint}
                                    onIssued={setIssued}
                                    onChanged={refresh}
                                />
                            ))}
                        </tbody>
                    </table>
                )}
            </article>
        </>
    );
};
